import Text from 'components/Text/Text';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useTranslation } from 'react-i18next';
import {
	DishAssignmentContext,
	IDishAssignmentContext,
} from '../../../../../dishAssignmentState/dishAssignmentContext';
import { UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { Select } from 'antd';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import SelectDropdownReloader from 'components/SelectDropdownReloader/SelectDropdownReloader';
import style from './RestaurantSelect.module.scss';

const { Option } = Select;

const RestaurantSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDSelectedRestaurant, setFetchedRestaurants, IDSelectedRestaurant, IDSelectedCustomer } =
		useContext<IDishAssignmentContext>(DishAssignmentContext);
	const parentNodeRef = useRef<null | HTMLSpanElement>(null);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);
	const { t } = useTranslation('listing');

	const activeRestaurants = useMemo(
		() => response?.data.restaurants.filter((restaurant) => restaurant.status === EntityStatus.Active),
		[response],
	);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	useEffect(() => {
		activeRestaurants && setFetchedRestaurants(activeRestaurants);
	}, [activeRestaurants]);

	const restaurants = useMemo(() => {
		return hasAccess
			? activeRestaurants
			: loggedUser.restaurants?.filter((restaurant) => restaurant.status === EntityStatus.Active);
	}, [hasAccess, activeRestaurants, loggedUser]);

	return (
		<div className={style.container} data-test="RestaurantSelect">
			<Text translationNameSpace="listing" translationKey="restaurantLabel" theme="neutralLight" size="xs" />
			<span ref={parentNodeRef} style={{ position: 'relative' }} />
			<Select
				className={style.select}
				onChange={(value) => setIDSelectedRestaurant(value ? value : null)}
				value={IDSelectedRestaurant}
				loading={loading}
				disabled={!IDSelectedCustomer || loading}
				notFoundContent={responseError ? <SelectDropdownReloader onReload={refetch} /> : undefined}
				showSearch={true}
				filterOption={(inputValue, option) =>
					option?.children ? String(option.children).toLowerCase().includes(inputValue.toLowerCase()) : false
				}
				showArrow={true}
				allowClear={true}
				listItemHeight={restaurants?.length}
				getPopupContainer={() => parentNodeRef.current as HTMLSpanElement}
				placeholder={t('selectPlaceholder')}>
				{restaurants?.length ? (
					<>
						<Option className={style.disabledItem} value="empty" disabled={true}>
							<Text
								translationNameSpace="listing"
								translationKey="selectAnyItemPlaceholder"
								asFragment={true}
							/>
						</Option>
						{restaurants.map((el) => (
							<Option value={el.id} key={el.id}>
								{el.name}
							</Option>
						))}
					</>
				) : null}
			</Select>
		</div>
	);
};

export default RestaurantSelect;
